import React from "react";
import { Box } from "@mui/joy";
import type { MailPlaceholderDefinition } from "api/mailTemplates/MailTemplate.typed";
import DarkCheckbox from "../../../common/DarkCheckbox";
import { colorTokens } from "../../../../styles/globalStyles";
import {
  EmailIcon,
  FacebookIcon,
  InstagramIcon,
  LanguageIcon,
  LinkedInIcon,
  PhoneIcon,
  WhatsAppIcon,
  YouTubeIcon,
} from "../../../../styles/StyledMUI";
import type { MailBodyBlock } from "../mailBodyBlocks";
import type { MailContactItem, MailContactKind } from "../mailContactBlock";
import {
  ensureMailContactItems,
  MAIL_CONTACT_KIND_LABELS,
  mailContactItemFieldKey,
  mailPlaceholderValueTypesForContactKind,
} from "../mailContactBlock";
import {
  mailLinePartsForSubjectField,
  parsePlainStringToMailLineParts,
  serializeMailLinePartsToSubject,
} from "../mailLineParts";
import MailRichLineEditor from "./MailRichLineEditor";

type ContactBlock = Extract<MailBodyBlock, { type: "contact" }>;

const KIND_ICON: Record<MailContactKind, React.ReactNode> = {
  phone: <PhoneIcon sx={{ fontSize: 22 }} />,
  email: <EmailIcon sx={{ fontSize: 22 }} />,
  website: <LanguageIcon sx={{ fontSize: 22 }} />,
  facebook: <FacebookIcon sx={{ fontSize: 22 }} />,
  instagram: <InstagramIcon sx={{ fontSize: 22 }} />,
  linkedin: <LinkedInIcon sx={{ fontSize: 22 }} />,
  youtube: <YouTubeIcon sx={{ fontSize: 22 }} />,
  whatsapp: <WhatsAppIcon sx={{ fontSize: 22 }} />,
};

const KIND_COLOR: Record<MailContactKind, string> = {
  phone: colorTokens.text.title,
  email: colorTokens.text.primary,
  website: colorTokens.text.primary,
  facebook: "#1877F2",
  instagram: "#E4405F",
  linkedin: "#0A66C2",
  youtube: "#FF0000",
  whatsapp: "#25D366",
};

type MailContactBlockFieldsProps = {
  block: ContactBlock;
  canEdit: boolean;
  onPatch: (b: MailBodyBlock) => void;
  setFieldRef: (key: string, el: HTMLElement | null) => void;
  trackFocus: (blockId: string, field: string) => void;
  presentablePhLabel: (key: string) => string;
  placeholderCatalog: MailPlaceholderDefinition[];
};

export default function MailContactBlockFields(props: MailContactBlockFieldsProps) {
  const {
    block,
    canEdit,
    onPatch,
    setFieldRef,
    trackFocus,
    presentablePhLabel,
    placeholderCatalog,
  } = props;
  const items = React.useMemo(() => ensureMailContactItems(block.items), [block.items]);

  const patchItems = React.useCallback(
    (nextItems: MailContactItem[]) => {
      onPatch({ ...block, items: ensureMailContactItems(nextItems) });
    },
    [block, onPatch],
  );

  const patchItem = (kind: MailContactKind, patch: Partial<MailContactItem>) => {
    patchItems(
      items.map((row) => {
        if (row.kind !== kind) {
          return row;
        }

        return { ...row, ...patch };
      }),
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
      {items.map((item) => {
        const fieldKey = mailContactItemFieldKey(item.kind);
        const parts = mailLinePartsForSubjectField(parsePlainStringToMailLineParts(item.value));

        return (
          <Box
            key={item.kind}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexWrap: { xs: "wrap", sm: "nowrap" },
              minWidth: 0,
              opacity: item.enabled ? 1 : 0.55,
            }}
          >
            <DarkCheckbox
              checked={item.enabled}
              disabled={!canEdit}
              size="sm"
              aria-label={MAIL_CONTACT_KIND_LABELS[item.kind]}
              onChange={(e) => patchItem(item.kind, { enabled: e.target.checked })}
              sx={{ flexShrink: 0 }}
            />
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: colorTokens.bg.surfaceMuted,
                color: KIND_COLOR[item.kind],
                flexShrink: 0,
              }}
              aria-hidden
            >
              {KIND_ICON[item.kind]}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, width: { xs: "100%", sm: "auto" } }}>
              <MailRichLineEditor
                parts={parts}
                onChange={(nextParts) =>
                  patchItem(item.kind, { value: serializeMailLinePartsToSubject(nextParts) })
                }
                canEdit={canEdit && item.enabled}
                blockId={block.id}
                editorFieldId={fieldKey}
                ariaLabel={MAIL_CONTACT_KIND_LABELS[item.kind]}
                setFieldRef={setFieldRef}
                trackFocus={trackFocus}
                variant="subject"
                minRowsText={1}
                presentablePhLabel={presentablePhLabel}
                placeholderCatalog={placeholderCatalog}
                placeholderValueTypes={mailPlaceholderValueTypesForContactKind(item.kind)}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
